import { useState, useEffect, useRef, useCallback } from 'react';
import cn from 'classnames';
import { Button, Typography, useIsMounted, useOverlayContext } from 'seagull';
import { useStoreActions } from "../../../contexts";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { handleRequest, wait } from "../../../infra/utils";
import { getAccountingDetails } from "../../../integrations/Accounting/accounting.utils";
import { accountingIconMap } from "../../../integrations/Accounting/AccountingIcon/accountingIconMap";
import { timeModel } from "../../../models";
import { useAppConfig } from "../../../store/store-context";
import { getPayoutAccountingErrorMessage } from "../../../utils/getAccountingInvoiceErrorMessage";
import styles from "./PayoutAccountingSection.module.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function getAccountingInfoStatus(payoutIntegration) {
  return payoutIntegration.accept({
    whenPendingSync: () => 'pending',
    whenSynced: () => 'success',
    whenSyncFailed: () => 'error',
    whenSyncInProgress: () => 'syncing',
    whenDeleted: () => 'success'
  });
}
const accountingFullName = accounting => getAccountingDetails(accounting).fullName;
export function getInfo(_ref) {
  let {
    payoutIntegration,
    accounting,
    featureFlags
  } = _ref;
  return payoutIntegration.accept({
    whenPendingSync: () => "Payout is ready to sync with ".concat(accountingFullName(accounting), "."),
    whenSyncInProgress: () => 'Sync in progress.',
    whenSynced: () => null,
    whenSyncFailed: () => getPayoutAccountingErrorMessage(payoutIntegration, accounting, featureFlags),
    whenDeleted: _ref2 => {
      let {
        date
      } = _ref2;
      return "Payout was deleted from ".concat(accountingFullName(accounting), " on ").concat(timeModel.formatDateWithTime(date), ".");
    }
  });
}
export default function PayoutAccountingSection(props) {
  const {
    payoutId,
    payoutIntegration,
    testId,
    reloadPayout,
    accounting
  } = props;
  const isMounted = useIsMounted();
  const overlayContext = useOverlayContext();
  const {
    qboPollingInterval
  } = useAppConfig();
  const {
    api
  } = useStoreActions();
  const [isSyncing, setIsSyncing] = useState(false);
  const status = getAccountingInfoStatus(payoutIntegration);
  const featureFlags = useFeatureFlags();
  const info = getInfo({
    payoutIntegration,
    accounting,
    featureFlags
  });
  const {
    PAYOUT_VIEW = false
  } = featureFlags;
  const payoutIntegrationRef = useRef(payoutIntegration);
  const openSyncToast = useCallback(type => {
    if (type === 'success') {
      overlayContext.showSuccessToast('Payout was successfully synced', 'sync-success-toast');
    } else {
      overlayContext.showErrorToast('Failed to sync payout', 'sync-failure-toast');
    }
  }, [overlayContext]);
  useEffect(() => {
    if (payoutIntegrationRef.current !== payoutIntegration) {
      if (PAYOUT_VIEW && isSyncing) {
        if (payoutIntegration.isSyncFailed()) {
          setIsSyncing(false);
          openSyncToast('failure');
        } else if (payoutIntegration.isSynced()) {
          setIsSyncing(false);
          openSyncToast('success');
        }
      }
      payoutIntegrationRef.current = payoutIntegration;
    }
  }, [payoutIntegration, PAYOUT_VIEW, isSyncing, openSyncToast]);
  async function syncPayout() {
    setIsSyncing(true);
    const {
      success
    } = await handleRequest(api.accounting.syncPayout({
      accounting,
      payoutId
    }));
    if (!isMounted()) return;
    if (!success) {
      setIsSyncing(false);
      openSyncToast('failure');
    } else if (!PAYOUT_VIEW) {
      await wait(qboPollingInterval);
      const newPayout = await reloadPayout(accounting);
      if (!isMounted()) return;
      setIsSyncing(false);
      newPayout.accountingIntegration(accounting) && openSyncToast(newPayout.accountingIntegration(accounting).isSyncFailed() ? 'failure' : 'success');
    }
  }
  if (status === 'success') {
    return null;
  }
  return /*#__PURE__*/_jsxs("div", {
    className: cn(styles.container, {
      [styles.error]: status === 'error'
    }),
    "data-testid": testId,
    "data-status": status,
    children: [/*#__PURE__*/_jsxs("div", {
      className: styles.status,
      children: [/*#__PURE__*/_jsx("div", {
        children: status && accountingIconMap[accounting][status]
      }), /*#__PURE__*/_jsx(Typography, {
        testId: testId && "".concat(testId, "-info"),
        children: info
      })]
    }), /*#__PURE__*/_jsxs("div", {
      className: styles.actions,
      children: [payoutIntegration.isSyncFailed() && /*#__PURE__*/_jsx(Button, {
        className: styles.retrySyncBtn,
        skin: "primaryOutline",
        size: "small",
        testId: "retry-sync-payout",
        loading: isSyncing,
        loadingText: "Syncing...",
        onClick: () => syncPayout(),
        children: "Retry sync"
      }), payoutIntegration.isPendingSync() && /*#__PURE__*/_jsx(Button, {
        className: styles.actionButton,
        testId: "sync-payout",
        onClick: () => syncPayout(),
        skin: "primaryOutline",
        size: "small",
        loading: isSyncing,
        loadingText: "Syncing...",
        children: "Sync"
      })]
    })]
  });
}